import { Icon } from '@iconify/react'
import { formatTHB } from '@/lib/utils'
import { cn } from '@/lib/utils'

interface AmountDisplayProps {
  /** Amount in satang (1/100 THB) */
  satang: number
  size?: 'sm' | 'md' | 'lg'
  showIcon?: boolean
  className?: string
}

export function AmountDisplay({
  satang,
  size = 'md',
  showIcon = false,
  className,
}: AmountDisplayProps) {
  const formatted = formatTHB(satang)

  const sizeStyles = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg font-semibold',
  }

  return (
    <span className={cn('text-slate-900 tabular-nums', sizeStyles[size], className)}>
      {showIcon && (
        <Icon icon="solar:hand-money-line-duotone" className="inline-block w-4 h-4 mr-1 text-slate-400" />
      )}
      {formatted}
    </span>
  )
}

// Range display for budget estimates
interface AmountRangeProps {
  minSatang?: number
  maxSatang?: number
  className?: string
}

export function AmountRange({ minSatang, maxSatang, className }: AmountRangeProps) {
  if (!minSatang && !maxSatang) return <span className="text-slate-400">-</span>

  if (minSatang && maxSatang && minSatang !== maxSatang) {
    return (
      <span className={cn('tabular-nums', className)}>
        {formatTHB(minSatang)} - {formatTHB(maxSatang)}
      </span>
    )
  }

  return (
    <span className={cn('tabular-nums', className)}>
      {formatTHB(minSatang || maxSatang || 0)}
    </span>
  )
}

// Compact amount for list views
interface CompactAmountProps {
  satang: number
  className?: string
}

export function CompactAmount({ satang, className }: CompactAmountProps) {
  const thb = satang / 100

  // Format as compact number
  const formatCompact = (num: number): string => {
    if (num >= 1_000_000_000) {
      return `${(num / 1_000_000_000).toFixed(1)}B`
    }
    if (num >= 1_000_000) {
      return `${(num / 1_000_000).toFixed(1)}M`
    }
    if (num >= 1_000) {
      return `${(num / 1_000).toFixed(1)}K`
    }
    return num.toFixed(0)
  }

  return (
    <span className={cn('text-slate-700 tabular-nums font-medium', className)}>
      ฿{formatCompact(thb)}
    </span>
  )
}
